/**
 * Tâche 1493 (Eric 2026-06) — Le menu « Continuer (connaissances) ».
 *
 * Remplace le bouton unique qui enchaînait fusion PUIS extraction sans
 * demander : refaire la fusion était le prix obligatoire pour extraire, soit
 * un appel de modèle sur un texte le plus souvent déjà fusionné.
 *
 * Trois options, dans l'ordre du pipeline, chacune nommée d'après ce qui a
 * RÉELLEMENT été fait (« Faire » ou « Refaire »), et chacune s'arrêtant
 * après son étape pour laisser l'utilisateur décider de la suite.
 *
 * ⚠️ Le contenu est chargé AU CLIC. Les trois écrans qui montent ce menu
 * affichent jusqu'à 200 lignes : interroger l'état des étapes pour chacune
 * aurait coûté 800 lectures à chaque rafraîchissement, pour un menu ouvert
 * une fois.
 * ⚠️ Confirmation seulement pour « Refaire » (choix d'Eric) : une étape
 * jamais faite part sans question, refaire une extraction coûte six appels
 * GPT et doit se dire.
 */
import { useCallback, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Play, ArrowsClockwise, Database } from "@phosphor-icons/react";

import { useLang } from "../../contexts/LanguageContext";
import { confirmDialog } from "../../lib/uiDialogs";
import { actionEtape } from "../../lib/etapesPipeline";
import ActionMenu from "./ActionMenu";

const ICONES = { fusion: ArrowsClockwise, extraction: Play, indexation: Database };

export default function MenuContinuerPipeline({
  api, video, etapes: etapesFournies, onFait, sizeClass, triggerClass,
}) {
  const { lang, tr } = useLang();
  const [etapes, setEtapes] = useState(etapesFournies || null);
  const [busy, setBusy] = useState(null);
  const etat = etapesFournies || etapes;

  const charger = useCallback(async () => {
    if (etapesFournies) return;
    try {
      const { data } = await axios.get(
        `${api}/api/videos/${video.id}/pipeline/etapes`, { withCredentials: true });
      setEtapes(data);
    } catch (err) {
      // Le menu reste ouvrable : ses libellés tombent alors sur « Faire »,
      // et le serveur refusera proprement une étape impossible.
      setEtapes({});
    }
  }, [api, video.id, etapesFournies]);

  const lancer = async (cle) => {
    const action = actionEtape(cle, etat || {}, lang);
    if (action.refaire) {
      const ok = await confirmDialog(
        `${action.label} ?\n\n${action.desc}\n\n${tr(
          "Cette étape a déjà été faite : la refaire consomme des crédits IA et remplace le résultat précédent.",
          "This step has already been done: redoing it consumes AI credits and replaces the previous result.",
        )}`,
      );
      if (!ok) return;
    }
    setBusy(cle);
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${video.id}/pipeline/etape/${cle}`, {},
        { withCredentials: true },
      );
      toast.success(data?.message || tr("Étape lancée", "Step started"), { duration: 7000 });
      if (onFait) await onFait();
      if (!etapesFournies) await charger();
    } catch (err) {
      toast.error(err?.response?.data?.detail
        || tr("Impossible de lancer cette étape", "Could not start this step"));
    } finally {
      setBusy(null);
    }
  };

  const items = ["fusion", "extraction", "indexation"].map((cle) => {
    const action = actionEtape(cle, etat || {}, lang);
    return {
      id: cle,
      label: action.label,
      desc: action.desc,
      empeche: etat?.en_cours
        ? tr("Un traitement est en cours sur cette vidéo.", "A job is already running on this video.")
        : action.empeche,
      onClick: () => lancer(cle),
      busyKey: cle,
      icon: ICONES[cle],
    };
  });

  return (
    <ActionMenu
      video={video}
      busy={busy}
      items={items}
      onOpen={charger}
      triggerLabel={tr("Continuer (connaissances)", "Continue (knowledge)")}
      triggerIcon={Play}
      triggerClass={triggerClass || "bg-teal-700 hover:bg-teal-600 border border-teal-600 text-white"}
      sizeClass={sizeClass}
      triggerTitle={tr(
        "Choisir l'étape par laquelle reprendre : fusion, extraction des connaissances ou indexation de la base",
        "Choose which step to resume from: fusion, knowledge extraction or database indexing",
      )}
      testidPrefix="menu-continuer"
    />
  );
}
